'use client';

import { FormEvent, useEffect, useState } from 'react';
import { Eye, EyeOff, Headphones, ShieldCheck } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { usePostgresAuth } from '../lib/secure-auth-postgres';

const inputClass = 'admin-auth-input h-[52px] w-full rounded-xl border border-white/[0.14] bg-white/[0.025] px-[15px] text-[15px] text-[#F5F0E8] outline-none transition placeholder:text-white/32 focus:border-[#FF6B4A]/75 focus:ring-4 focus:ring-[#FF6B4A]/10 disabled:cursor-not-allowed disabled:opacity-55';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { user, loading, signIn, isAdmin } = usePostgresAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && user && isAdmin) router.push('/dashboard');
  }, [user, loading, isAdmin, router]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const userData = await signIn(email, password);
      if (userData?.level === 1) router.push('/dashboard');
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="grid min-h-dvh place-items-center bg-[#10110F] text-[#F5F0E8]">
        <div className="text-center">
          <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-2 border-white/15 border-t-[#FF6B4A]" />
          <p className="text-sm text-white/48">Vérification de votre session…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh bg-[#10110F] text-[#F5F0E8]">
      <section className="mx-auto flex min-h-dvh w-full max-w-[548px] items-center px-6 py-[18px] sm:px-8">
        <div className="w-full max-w-[500px]">
          <Brand />
          <div className="pt-[42px] pb-6">
            <h1 className="font-serif text-[43px] font-bold leading-none tracking-[-0.025em]">Bienvenue</h1>
            <p className="mt-[13px] max-w-[340px] text-base leading-[1.65] text-white/[0.62]">Connectez-vous à votre espace d’administration Annotap.</p>
            <Waveform className="mt-4 h-12 w-full" />
          </div>

          <div className="mb-6 border-b border-white/10 pb-5">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl border border-[#FF6B4A]/25 bg-[#FF6B4A]/10 text-[#FF6B4A]">
                <ShieldCheck size={20} strokeWidth={2.1} />
              </span>
              <div>
                <h2 className="font-serif text-2xl font-semibold tracking-tight">Administration</h2>
                <p className="mt-1 text-sm text-white/42">Accès réservé aux administrateurs</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Adresse e-mail">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={inputClass}
                placeholder="vous@exemple.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isLoading}
              />
            </Field>

            <Field label="Mot de passe">
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={`${inputClass} pr-12`}
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 grid w-12 place-items-center text-white/38 transition hover:text-white focus:outline-none focus:text-white"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </Field>

            <div aria-live="polite" className={`min-h-5 text-sm ${error ? 'text-[#FF8A70]' : 'text-white/35'}`}>
              {error}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="h-14 w-full rounded-xl bg-[#FF6B4A] font-bold text-[#17130F] transition hover:bg-[#FF7B5E] focus:outline-none focus:ring-4 focus:ring-[#FF6B4A]/25 disabled:cursor-not-allowed disabled:opacity-55"
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#17130F]/25 border-t-[#17130F]" />
                  Connexion…
                </span>
              ) : 'Se connecter'}
            </button>
          </form>

          <p className="mt-[23px] text-center text-xs leading-relaxed text-white/[0.38]">
            Cette interface contient des données sensibles. Vérifiez toujours l’adresse avant de vous connecter.
          </p>
        </div>
      </section>
    </main>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#FF6B4A] text-[#141310]">
        <Headphones size={21} strokeWidth={2.4} />
      </span>
      <span className="font-serif text-2xl font-semibold tracking-tight">annotap</span>
    </div>
  );
}

function Waveform({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 560 64" fill="none" aria-hidden="true">
      <path d="M0 32h16m0 0v-9m0 9v9m8-9V14m0 18v18m8-18V6m0 26v52m8-52V18m0 14v28m8-28V11m0 21v42m8-42V20m0 12v24m8-24v-8m0 8v8m0-8h16c20 0 22 22 44 22s30-22 52-22h358" stroke="#FF6B4A" strokeWidth="2" strokeLinecap="round" />
      <circle cx="556" cy="32" r="4" fill="#FF6B4A" />
    </svg>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-white/76">{label}</span>
      {children}
    </label>
  );
}
